import { XF } from "./XF";
import { getBrowser } from "./preambleBrowser";
import "./preambleColorScheme";
import { readCookie } from "./preambleReadCookie";
import { getItemFromLocal, setItemInLocal } from "./preambleStorage";

function noop() {
  // intentionally left blank
}

// Deal with the console not being present
if (!window.console) window.console = {} as (typeof window)["console"];
if (window.console.log && !window.console.debug)
  window.console.debug = window.console.log;
(
  [
    "assert",
    "clear",
    "count",
    "debug",
    "dir",
    "dirxml",
    "error",
    "getFirebugElement",
    "group",
    "groupCollapsed",
    "groupEnd",
    "info",
    "log",
    "notifyFirebug",
    "profile",
    "profileEnd",
    "time",
    "timeEnd",
    "trace",
    "warn",
  ] as const
).forEach((fn) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (window.console[fn]) return;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  window.console[fn] = noop;
});

// IMPORTANT - jQuery is not available here!
// Keep this code minimal and only for things that need to happen in the <head> tag.

const docEl = document.documentElement;
docEl.addEventListener(
  "error",
  (e) => {
    const { target } = e;
    const onerror = target.getAttribute("data-onerror");
    switch (onerror) {
      case "hide":
        target.style.display = "none";
        break;

      case "hide-parent":
        target.parentNode.style.display = "none";
        break;

      default:
        break;
    }
  },
  true
);

XF.Feature = (function Feature() {
  function hasTouchevents() {
    let supportTouch = getItemFromLocal("supportTouch");
    if (supportTouch !== false) {
      return supportTouch === "true";
    }

    supportTouch =
      "ontouchstart" in window ||
      (window.DocumentTouch && document instanceof window.DocumentTouch);

    setItemInLocal("supportTouch", JSON.stringify(supportTouch));

    return supportTouch;
  }

  function hasPassiveeventlisteners() {
    const supportPassiveEventListeners = getItemFromLocal(
      "supportPassiveEventListeners"
    );
    if (supportPassiveEventListeners !== false) {
      return supportPassiveEventListeners === "true";
    }

    let passiveEventListeners = false;

    try {
      const opts = Object.defineProperty({}, "passive", {
        get() {
          passiveEventListeners = true;
        },
      });
      window.addEventListener("test", noop, opts);
      window.removeEventListener("test", noop, opts);
    } catch (e) {
      console.error(e);
    }

    setItemInLocal(
      "supportPassiveEventListeners",
      JSON.stringify(passiveEventListeners)
    );

    return passiveEventListeners;
  }

  function hasHiddenscroll() {
    const supportHiddenscroll = getItemFromLocal("supportHiddenscroll");
    if (supportHiddenscroll !== false) {
      return supportHiddenscroll === "true";
    }

    let { body } = document;
    let fake = false;

    if (!body) {
      body = document.createElement("body");
      document.body = body;
      fake = true;
    }

    const div = document.createElement("div");
    div.style.width = "100px";
    div.style.height = "100px";
    div.style.overflow = "scroll";
    div.style.position = "absolute";
    div.style.top = "-9999px";

    body.appendChild(div);

    const hiddenscroll = div.offsetWidth === div.clientWidth;

    if (fake) {
      body.parentNode.removeChild(body);
    } else {
      div.parentNode.removeChild(div);
    }

    setItemInLocal("supportHiddenscroll", JSON.stringify(hiddenscroll));

    return hiddenscroll;
  }

  const testResults: { [key: string]: unknown } = {};
  const checkedTest: { [key: string]: true | undefined } = {};
  let firstRun = true;

  function init() {
    if (firstRun) {
      docEl.className = docEl.className.replace(
        /(^|\s)has-no-js($|\s)/,
        "$1has-js$2"
      );
      firstRun = false;
    }
  }

  function runTest(name: string, result: unknown) {
    if (!checkedTest[name]) {
      testResults[name] = result;
      checkedTest[name] = true;
      docEl.className += ` has-${!testResults[name] ? "no-" : ""}${name}`;
    }
  }

  function has(name: string) {
    switch (name) {
      case "touchevents":
        runTest(name, hasTouchevents());
        break;
      case "hiddenscroll":
        runTest(name, hasHiddenscroll());
        break;
      case "passiveeventlisteners":
        runTest(name, hasPassiveeventlisteners());
        break;
      default:
        break;
    }

    if (typeof testResults[name] === "undefined") {
      console.error(`Asked for unknown test results: ${name}`);
      return false;
    }
    return testResults[name];
  }

  return {
    init,
    has,
    hasPassiveeventlisteners,
    hasTouchevents,
    hasHiddenscroll,
  };
})();

XF.Feature.init();

const savedBrowserOriginal = getItemFromLocal("browserOriginal");
if (savedBrowserOriginal !== false) {
  XF.browser = JSON.parse(savedBrowserOriginal);
} else {
  XF.browser = getBrowser();
  setItemInLocal("browserOriginal", JSON.stringify(XF.browser));
}

XF.geoCountry = (readCookie("client_country_code") ?? "").toLowerCase();
XF.geoRegion = (readCookie("client_state_code") ?? "").toLowerCase();
XF.isSuspectedBot = (readCookie("is_suspected_bot") ?? "") === "1";
XF.isForaMobileApp = navigator.userAgent.indexOf("Fora Mobile App") > -1;
try {
  const cacheState = (
    window.performance.getEntriesByType(
      "navigation"
    ) as PerformanceNavigationTiming[]
  )
    .flatMap((i) => i.serverTiming)
    .find((i) => i.name === "cache")?.description;
  switch (cacheState) {
    case "hit":
      XF.isServedFromCache = true;
      break;
    case "miss":
      XF.isServedFromCache = false;
      break;
    default:
      XF.isServedFromCache = undefined;
      break;
  }
} catch (err) {
  XF.isServedFromCache = undefined;
}

document.documentElement.dataset.os = XF.browser.os ?? "";
document.documentElement.dataset.geoCountry = XF.geoCountry;
document.documentElement.dataset.geoRegion = XF.geoRegion;
document.documentElement.dataset.isForaMobileApp =
  XF.isForaMobileApp.toString();

const app = docEl.getAttribute("data-app");
const loggedIn = docEl.getAttribute("data-logged-in") === "true";

if (app === "public" && !loggedIn) {
  // prevent page jumping from dismissed notices for guests
  const dismissedNoticeCookie = readCookie("notice_dismiss");
  const dismissedNotices = dismissedNoticeCookie
    ? dismissedNoticeCookie.split(",")
    : [];
  let noticeId;
  const selectors = [];

  for (let i = 0; i < dismissedNotices.length; i++) {
    noticeId = parseInt(dismissedNotices[i], 10);
    if (noticeId !== 0) {
      selectors.push(`.notice[data-notice-id="${noticeId}"]`);
    }
  }

  if (selectors.length) {
    const el = document.createElement("style");
    el.type = "text/css";
    el.innerHTML = `${selectors.join(", ")} { display: none !important } `;
    document.head.appendChild(el);
  }
}
